import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ColorSchemes } from 'src/styles/color-scheme.type';
import { PendingTaskCounter } from '../../classes/pending-task-counter';
import { FileInStorageHlp } from '../../helpers/file.helper';
import { FileData, FileInStorageVM, FileSummaryData } from '../../models/file.data';
import { Parser } from '../../parsers/parser.interface';
import { DeleteFileService } from '../../services/delete-file.service';
import { FireStorageService } from '../../services/storage.service';

@Component({
  selector: 'app-add-attachment-field',
  templateUrl: './add-attachment-field.component.html',
  styleUrls: ['./add-attachment-field.component.scss']
})
export class AddAttachmentFieldComponent implements OnDestroy {
  @Input() set attachments(att: FileInStorageVM[]) { this.setAttachments(att); } // prettier-ignore
  @Input() parsers: Parser[] = [];
  @Input() uploadFilePath!: string;
  @Input() maxFilesCount: number = 10;
  @Input() fileSizeLimit: number = 0; // 0 means no limit
  @Input() fileTypes = ['.pdf', '.xls', '.xlsx', '.doc', '.docx', '.odt', '.ods', '.rtf'];
  @Input() colors: ColorSchemes = ColorSchemes.PRIMARY;

  @Input() showDeleteButton: boolean = true;
  @Input() disabled: boolean = false;
  @Input() showFileSize: boolean = true;

  @Output() onSumSizeChange = new EventEmitter<number>();

  public filesToUpload: FileData[] = [];
  public fileSizeSum = 0;
  public maxCountExceeded = false;

  private _attWoSize: FileInStorageVM[] = [];
  private _attWithSize: FileInStorageVM[] = [];
  private readonly pendingFiles = new PendingTaskCounter();
  private currentAttachmentsLoading = false;
  private attachmentsLoadVersion = 0;

  ColorSchemes = ColorSchemes;
  constructor(
    private fs: FireStorageService,
    private deleteFileService: DeleteFileService
  ) {}

  ngOnDestroy(): void {
    this.pendingFiles.clear();
    this.deleteFileService.clearFilesToDelete();
  }

  public get files(): FileInStorageVM[] {
    return this.showFileSize ? this._attWithSize : this._attWoSize;
  }

  private set files(val: FileInStorageVM[]) {
    if (this.showFileSize) this._attWithSize = val;
    else this._attWoSize = val;
  }

  public get filesCount(): number {
    const currentFilesCount = this.showFileSize && this.currentAttachmentsLoading ? this._attWoSize.length : this.files.length;
    return currentFilesCount + this.filesToUpload.length + this.pendingFiles.count;
  }

  private get availableFilesCount(): number {
    return Math.max(this.maxFilesCount - this.filesCount, 0);
  }

  public addFiles(files: File[]): void {
    const availableFilesCount = this.availableFilesCount;
    const maxCountExceeded = files.length > availableFilesCount;
    files.slice(0, availableFilesCount).forEach((file) => this.addFile(file));
    this.maxCountExceeded = maxCountExceeded;
  }

  public addFile(file: File): void {
    if (!file) return;
    if (!this.availableFilesCount) {
      this.maxCountExceeded = true;
      return;
    }

    this.maxCountExceeded = false;
    const completePendingFile = this.pendingFiles.start();

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        let fileData: string = reader.result as string;
        let fileName: string = file.name;
        let fileToSave = file;
        const extension = fileName.split('.').pop() ?? 'txt';
        if (this.parsers.length) {
          const parser = this.parsers.find((p) => p.inputExtensions.includes(extension));
          if (parser) {
            const parsedData = await parser.parse(reader.result);
            if (parsedData) {
              fileData = parsedData;
              fileName = fileName.replace(`.${extension}`, `.${parser.outputExtension}`);
              fileToSave = new File([fileData], fileName, { type: 'text/csv' });
            }
          }
        }
        this.filesToUpload = [...this.filesToUpload, { file: fileToSave, rawData: fileData, name: fileName, size: file.size }];
      } finally {
        completePendingFile();
        this.updateFileSizeSum();
      }
    };
    reader.onerror = completePendingFile;
    reader.onabort = completePendingFile;
    try {
      reader.readAsArrayBuffer(file);
    } catch (err) {
      completePendingFile();
      throw err;
    }
  }

  public removeFile(index: number, uploaded: boolean): void {
    if (uploaded) {
      this.filesToUpload = this.filesToUpload.filter((_, i) => i !== index);
      this.maxCountExceeded = false;
      this.updateFileSizeSum();
    } else {
      const attToDelete = { ...this.files[index] };
      this.deleteFileService.confirmDelete$(attToDelete.name, this.colors).subscribe((result) => {
        if (result) {
          this.deleteFileService.addFileToDelete(attToDelete.storagePath);
          this.files = this.files.filter((_, i) => i !== index);
          this.maxCountExceeded = false;
          this.updateFileSizeSum();
        }
      });
    }
  }

  /**
   * ! USED IN PARENT COMPONENT
   * @return List of current and to upload attachments
   */
  public getSummaryData(): FileSummaryData {
    return {
      current: this.currentAttachmentsLoading ? this._attWoSize : this.files,
      toUpload: this.filesToUpload
    };
  }

  public whenIdle(): Promise<void> {
    return this.pendingFiles.whenIdle();
  }

  /**
   * ! USED IN PARENT COMPONENT
   * @param {boolean} url flag that indicates if function returns FileInStorageVM with downloadURL
   * @return Attachments added and existing (WITHOUT deleted)
   */
  public async onSubmit(url: boolean = true): Promise<FileInStorageVM[]> {
    await this.whenIdle();

    return [
      ...this.getSummaryData().current,
      ...(await this.fs.uploadFilesToStorage(
        this.filesToUpload.map((f) => f.file!),
        this.uploadFilePath,
        url
      ))
    ];
  }

  /**
   * Checks if sum of file sizes is less than fileSizeLimit (if exists)
   * @return
   */
  public validSize(): boolean {
    return this.fileSizeLimit ? this.fileSizeSum <= this.fileSizeLimit : true;
  }

  // ! Should be always called after [dbObject].modify()
  public async handleDeleted(): Promise<void> {
    await this.deleteFileService.deleteFiles();
  }

  public getUploadedAttachments(): FileData[] {
    return this.filesToUpload;
  }

  private setAttachments(att: FileInStorageVM[]): void {
    this._attWoSize = att;
    const loadVersion = ++this.attachmentsLoadVersion;
    if (!this.showFileSize) {
      this.currentAttachmentsLoading = false;
      return;
    }

    this.currentAttachmentsLoading = true;
    FileInStorageHlp.copyAndAppendFiles(att, this._attWithSize, this.fs).then((copy) => {
      if (loadVersion !== this.attachmentsLoadVersion) return;

      this._attWithSize = copy;
      this.currentAttachmentsLoading = false;
      if (this.fileSizeLimit) this.updateFileSizeSum();
    });
  }

  private updateFileSizeSum(): void {
    const upl = this.filesToUpload.reduce((prev, curr) => prev + curr.file!.size, 0);
    const att = this.files.reduce((prev, curr) => prev + (curr?.size ?? 0), 0);
    this.fileSizeSum = upl + att;
    this.onSumSizeChange.emit(this.fileSizeSum);
  }
}
